"use client";

import { ArrowRight, ShieldCheck, Clock3, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { makeServiceT } from "@/data/serviceDetails";
import PricingCards from "@/components/landing/PricingCards";

const Pricing = () => {
  const { t } = useTranslation();

  const plans = [
    {
      name: t("pricing.essentialsName"),
      price: "$25",
      period: t("pricing.perMonth"),
      subtitle: t("pricing.essentialsSubtitle"),
      services: ["Telemedicine", "Dermatology"].map((n) => makeServiceT(n, t)),
      popular: false,
    },
    {
      name: t("pricing.advantageName"),
      price: "$35",
      period: t("pricing.perMonth"),
      subtitle: t("pricing.advantageSubtitle"),
      services: [
        "Telemedicine",
        "Dermatology",
        "Behavioral Health",
        "Pharmacy Savings",
        "Healthcare Concierge",
      ].map((n) => makeServiceT(n, t)),
      popular: false,
    },
    {
      name: t("pricing.premierName"),
      price: "$45",
      period: t("pricing.perMonth"),
      subtitle: t("pricing.premierSubtitle"),
      services: [
        "Telemedicine",
        "Dermatology",
        "Behavioral Health",
        "Pharmacy Savings",
        "Healthcare Concierge",
        "Lab Testing",
        "Wellness Coaching",
        "Best Price MRI/Imaging",
        "Medical Bill Negotiations",
        "Medical Bill Financing",
      ].map((n) => makeServiceT(n, t)),
      popular: false,
    },
  ];

  /* ── Trust signals (visual strip, no copy change to pricing cards) ── */
  const trustItems = [
    { icon: Ban,          text: t("pricing.noContracts") },
    { icon: Clock3,       text: t("pricing.available247")         },
    { icon: ShieldCheck,  text: t("pricing.cancelAnytime")         },
  ];

  return (
    /* Slightly off-white background distinguishes section from neighbors */
    <section className="bg-[#f5f8fc] py-16 px-6 lg:px-12" id="pricing">
      <div className="max-w-[1100px] mx-auto">

        {/* ── Section header ─────────────────────────────── */}
        <div className="text-center mb-10">
          <h2 className="font-heading font-bold text-2xl lg:text-[32px] text-foreground mb-6">
            {t("pricing.heading")}
          </h2>

          {/* ── Trust strip (moved above cards) ─────────── */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 sm:gap-10 py-5 border-t border-b border-foreground/[0.07]">
            {trustItems.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2 text-foreground/55">
                <Icon className="w-4 h-4 text-accent-blue flex-shrink-0" />
                <span className="font-body text-sm">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Pricing cards (extra bottom padding for lifted popular card) ── */}
        <div className="mb-6 pb-8">
          <PricingCards plans={plans} ctaAction={() => window.open("https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs", "_blank")} />
        </div>

        {/* ── Section-level CTAs ─────────────────────────── */}
        <div className="text-center">
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="https://support.legacy-hc.com/widget/form/TgnMv3pkwXzQzfAvbwXs" target="_blank" rel="noopener noreferrer">
              <Button className="bg-accent-blue hover:bg-accent-blue/90 text-white rounded-full px-6 py-3 font-button text-sm cta-hover">
                {t("pricing.ctaFamily")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="https://calendly.com/astuhr-legacy-hc/telemedicine" target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="border-accent-blue text-accent-blue hover:bg-accent-blue hover:text-white rounded-full px-6 py-3 font-button text-sm cta-hover"
              >
                {t("pricing.ctaBusiness")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
          </div>
          <p className="mt-4 text-xs text-gray-500 max-w-xl mx-auto leading-relaxed">
            {t("pricing.membershipDisclaimer")}
          </p>
        </div>

      </div>
    </section>
  );
};

export default Pricing;
